import { concat, get, isEmpty, merge, omit, range } from "lodash";
import { PlotData, Layout, PlotType } from "plotly.js";
import { doHidden, COLORWAY } from "../utils";
import { IWaterfallFigure, IWaterfallStructure, IWaterfallChart, IDisplay, IResult } from "../types";
import { IOutputProps } from "./types";
import "./style.scss";
import { useMemo } from "react";
import ArchiveIcon from "./ArchiveIcon";
import { downloadData } from "./download";
import { ChartLoadingSkeleton } from "./loading";
import { isTemplateRefreshing } from "../dependencyIndex";
import RefreshingState from "../RefreshingState";
import AutoSizedPlot from "./AutoSizedPlot";

export interface IWaterfallChartProps extends IOutputProps {
  template: IWaterfallChart;
}

interface IPlotData extends PlotData {
  measure: Array<"absolute" | "relative" | "total">;
}

const renderLabel = (display: IDisplay) => {
  const label = display.label ? display.label : "";
  const info = display.info ? ` <span class="pb3-text-muted">${display.info}</span>` : "";
  return `${label}${info}`;
};

const assignMeasure = (
  template: IWaterfallChart,
  y: Array<number>,
  m: Array<"absolute" | "relative" | "total"> | undefined
) => {
  if (template.measure?.length === y.length) {
    return template.measure;
  } else if (m?.length === y.length) {
    return m;
  } else {
    return y.map((v, i) => (i === 0 ? "absolute" : v === null ? "total" : "relative"));
  }
};

const calculateTotals = (t: { v: number }, y: Array<number>, m: Array<"absolute" | "relative" | "total">) => {
  return range(0, Math.min(y.length, m.length)).map((i) => {
    switch (m[i]) {
      case "relative":
        t.v += y[i];
        return y[i];
      case "absolute":
        t.v = y[i];
        return y[i];
      case "total":
        return t;
      default:
        throw new Error(`Unknown measure type passed to switch statement: ${m[i]}`);
    }
  });
};

const createData = (figure: IWaterfallStructure | IWaterfallFigure, result: IResult) => {
  if ((figure as IWaterfallStructure).type === "structure") {
    const f = figure as IWaterfallStructure;
    return {
      name: renderLabel(f),
      measure: f.m_value
        ? ((get(result, f.name, []) as Array<any>).map((v) => v[f.m_value as string]) as Array<
            "absolute" | "relative" | "total"
          >)
        : undefined,
      x: (get(result, f.name, []) as Array<any>).map((v) => v[f.x_value]),
      y: (get(result, f.name, []) as Array<any>).map((v) => v[f.y_value]),
    };
  } else {
    const f = figure as IWaterfallFigure;
    return {
      name: renderLabel(f.y_data),
      measure: f.m_data ? (get(result, f.m_data.name, []) as Array<"absolute" | "relative" | "total">) : undefined,
      x: get(result, f.x_data.name, []) as Array<number | string>,
      y: get(result, f.y_data.name, []) as Array<number>,
    };
  }
};

const transformData = (template: IWaterfallChart, result: IResult): Array<Partial<IPlotData>> => {
  const t = { v: 0 };
  let data = template.figures
    .map((f) => {
      if (!template.concatenate) {
        t.v = 0;
      }
      const d = createData(f, result);
      d.measure = assignMeasure(template, d.y, d.measure);
      d.y = calculateTotals(t, d.y, d.measure);
      return {
        type: "waterfall" as PlotType,
        orientation: "v",
        textposition: "auto",
        connector: {
          line: {
            color: "rgb(63, 63, 63)",
          },
        },
        decreasing: { marker: { color: COLORWAY[1] } },
        increasing: { marker: { color: COLORWAY[3] } },
        totals: { marker: { color: COLORWAY[0] } },
        ...d,
      } as Partial<IPlotData>;
    })
    // Puts text on the chart but doesn't always layout well
    // .map((v) => {
    //   v.text = range(0, Math.min((v as any).measure.length, v.y?.length || 0)).map((i) =>
    //     (v as any).measure[i] === "total" ? "Total" : `${v.y?.[i]}`
    //   );
    //   return v;
    // })
    .filter((v) => !isEmpty(v.x) && !isEmpty(v.y));
  if (template.concatenate && data.length > 1) {
    data = data.reduce(
      (p, c) => {
        const t = p[0];
        t.x = concat(t.x as any[], c.x);
        t.y = concat(t.y as any[], c.y);
        t.measure = concat(t.measure as any[], c.measure);
        return p;
      },
      [merge(omit(data[0], ["x", "y", "measure"]), { x: [], y: [], measure: [] })] as [Partial<IPlotData>]
    );
  }
  return data;
};

const createLayout = (template: IWaterfallChart, result: IResult): Partial<Layout> => {
  const minimal = template.variant === "minimal";
  const [xaxis, yaxis] = !minimal
    ? [
        template.axis?.[0] || (template.figures?.[0] as IWaterfallFigure).x_data,
        template.axis?.[1] || (template.figures?.[0] as IWaterfallFigure).y_data,
      ]
    : [];
  const [xrange, yrange] = template.range || [];
  return merge(
    {
      title: template.label,
      colorway: COLORWAY,
      waterfallgap: 0.1,
      waterfallgroupgap: 0.1,
    },
    xaxis && { xaxis: { title: renderLabel(xaxis) } },
    yaxis && { yaxis: { title: renderLabel(yaxis) } },
    xrange && { xaxis: { range: get(result, xrange) } },
    yrange && { yaxis: { range: get(result, yrange) } }
  );
};

const WaterfallChart = (props: IWaterfallChartProps) => {
  const { template, result, metadata } = props;
  const data = useMemo(() => transformData(template, result), [template, result]);
  const layout = useMemo(() => createLayout(template, result), [template, result]);
  const refreshing = isTemplateRefreshing(template, metadata, props.mutators.getPath);

  if (doHidden(props)) {
    return null;
  }

  if (isEmpty(data) && refreshing) {
    return (
      <ChartLoadingSkeleton
        className={template.height ? "chart-container" : "small-chart-container"}
        height={template.height}
        label={template.label || template.name}
        variant="bar"
      />
    );
  }

  if (isEmpty(data)) {
    return null;
  }

  return (
    <RefreshingState refreshing={refreshing}>
    <div
      className={template.height ? "chart-container" : "small-chart-container"}
      {...(template.height && { style: { height: `${template.height}px` } })}
    >
      <AutoSizedPlot
        chartId={template.name}
        data={data}
        layout={layout}
        config={{
          displaylogo: false,
          modeBarButtonsToRemove: ["select2d", "lasso2d", "autoScale2d"],
          modeBarButtonsToAdd: [
            {
              name: "downloadData",
              title: "Download data as a csv",
              icon: ArchiveIcon,
              click: () => downloadData(template, data),
            },
          ],
        }}
      />
    </div>
    </RefreshingState>
  );
};

export default WaterfallChart;
